import BlobViewer from "~/js/asset-viewport/viewers/blob-viewer";
import { $div } from "~/js/utilities/TurboDom";
import AudioViewer from "./viewers/audio-viewer";
import ImageViewer from "./viewers/image-viewer";
import Object3DViewer from "./viewers/object3d-viewer";
import SvgViewer from "./viewers/svg-viewer";
import TextViewer from "./viewers/text-viewer";
import VideoViewer from "./viewers/video-viewer";

const AvailableViewers = [
	TextViewer,
	ImageViewer,
	AudioViewer,
	VideoViewer,
	Object3DViewer,

	BlobViewer,
	SvgViewer
];

export function getAvailableExtensionsByFileType(type) {
	for (const viewer of AvailableViewers) {
		if (viewer.type === type) {
			return viewer.supportedExtensions;
		}
	}
	return null;
}

/**
 * Wrapper for viewers to load different type asset
 * @param {File} asset
 * @param {any} node
 */
export default class AssetViewport {
	constructor(asset = null, socketsOutput = null, activeSocketName = null) {
		this.domElement = document.createElement("div");
		this.socketsOutput = socketsOutput;
		this.domElement.classList.add("asset-viewport");
		this.title = false;
		this.activeSocketName = activeSocketName;
		if (asset) this.loadAssetAsync(asset);
	}

	async loadAssetAsync(asset) {
		if (!asset) {
			this.clear();
			return;
		}
		if (!asset.name) {
			console.error(
				`[AssetViewport] Asset's name is undefined and we can't get extensions to initialize viewer`
			);
		}

		const extension = asset.name.split(".").pop().toLowerCase();

		for (const availableViewer of AvailableViewers) {
			if (!availableViewer.supportedExtensions.includes(extension)) continue;

			if (this.viewer && this.viewer.constructor.type === availableViewer.type) {
				await this.viewer.reloadAssetAsync(asset);
				return;
			}
			this.domElement.classList.add(availableViewer.type);

			this.clear();

			this.viewer = new availableViewer(
				asset,
				this.socketsOutput,
				this.activeSocketName,
				this.socketsOutput?.optionalInfo?.cameraPos,
				(value) => {
					this.socketsOutput.optionalInfo.cameraPos = value;
				}
			);

			if (this.title) this.viewer.setTitle(this.title);

			await this.viewer.loadAsync();
			this.domElement.append(this.viewer.domElementAppend());

			return;
		}
		console.error(`[AssetViewport] There is no viewers for such file format: ${extension}`);
	}

	setTitle(title) {
		this.title = title;
	}

	remove() {
		this.domElement.remove();
		this.clear();
	}

	clear() {
		if (this.viewer) {
			this.viewer.remove?.();
			this.viewer.domElementAppend().remove();
			this.viewer = undefined;
		}
	}

	highlight() {
		this.domElement.classList.add("highlighted-viewport");
	}

	unhighlight() {
		this.domElement.classList.remove("highlighted-viewport");
	}
}

export class MultiAssetViewport extends AssetViewport {
	constructor(asset = null, socketsOutput = null) {
		super(asset, socketsOutput);
		this.socketsOutput = undefined;
		this.activeIndex = 0;

		this.controlLeftEl = $div("viewport_control left", this.domElement);
		this.controlRightEl = $div("viewport_control right", this.domElement);

		this.setControlsHidden(!(!!asset?.length > 1));
		this.controlLeftEl.addEventListener("click", () => this.moveActiveViewport(false));
		this.controlRightEl.addEventListener("click", () => this.moveActiveViewport(true));
	}

	moveActiveViewport(positive) {
		if (positive) {
			if (this.activeIndex === this.viewers.length - 1) {
				this.setActiveIndex(0);
			} else {
				this.setActiveIndex(this.activeIndex + 1);
			}
		} else {
			if (this.activeIndex === 0) {
				this.setActiveIndex(this.viewers.length - 1);
			} else {
				this.setActiveIndex(this.activeIndex - 1);
			}
		}
	}

	setActiveIndex(index) {
		const previousSocket = this.sockets[this.activeIndex];
		if (previousSocket) {
			previousSocket.layout.domElement().classList.remove("active");
			this.viewers[this.activeIndex].domElementAppend().classList.add("hidden");
		}

		this.sockets[index].layout.domElement().classList.add("active");
		this.viewers[index].domElementAppend().classList.remove("hidden");

		this.activeIndex = index;
	}

	deselectCurrentIndex() {
		const previousSocket = this.sockets[this.activeIndex];
		const previousViewer = this.viewers[this.activeIndex];

		previousSocket?.layout.domElement().classList.remove("active");
		previousViewer?.domElementAppend().classList.add("hidden");
	}

	setControlsHidden(boolean) {
		this.controlLeftEl.classList.toggle("hidden", boolean);
		this.controlRightEl.classList.toggle("hidden", boolean);
	}

	async loadAssetAsync(sockets) {
		this.clear();

		for (let i = 0; i < sockets.length; i++) {
			const asset = sockets[i].value;

			if (!asset) {
				return;
			}
			if (!asset.name) {
				console.error(
					`[AssetViewport] Asset's name is undefined and we can't get extensions to initialize viewer`
				);
			}

			const extension = asset.name.split(".").pop().toLowerCase();

			for (const availableViewer of AvailableViewers) {
				if (!availableViewer.supportedExtensions.includes(extension)) continue;

				this.domElement.classList.add(availableViewer.type);

				const newViewer = new availableViewer(
					asset,
					sockets[i],
					sockets[i].name,
					sockets[i]?.optionalInfo?.cameraPos,
					(value) => {
						sockets[i].optionalInfo.cameraPos = value;
					}
				);
				this.viewers.push(newViewer);

				if (this.title) newViewer.setTitle(this.title);

				await newViewer.loadAsync();

				i !== this.activeIndex && newViewer.domElementAppend().classList.add("hidden");

				this.domElement.append(newViewer.domElementAppend());
				break;
			}
		}
		if (this.sockets?.length > 0) {
			this.deselectCurrentIndex();
		}
		this.sockets = [...sockets];

		if (this.sockets.length < 2) {
			this.setControlsHidden(true);
		} else {
			this.setControlsHidden(false);
		}
		if (this.sockets.length > 0) {
			this.setActiveIndex(0);
		}
	}

	setTitle(title) {
		this.title = title;
	}

	clear() {
		if (!this.viewers) {
			this.viewers = [];
			return;
		}

		for (const viewer of this.viewers) {
			if (viewer) {
				viewer.remove?.();
				viewer.domElementAppend().remove();
			}
		}
		this.viewers = [];
	}
}
